import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixFastgalleryDraftitemV1DraftItemServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'editor._base_domain_': [
      {
        srcPath: '/_api/draft-item',
        destPath: '',
      },
      {
        srcPath: '/draft-item',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/draft-item',
        destPath: '',
      },
      {
        srcPath: '/draft-item',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/draft-item',
        destPath: '',
      },
      {
        srcPath: '/draft-item',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/draft-item',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/draft-item',
        destPath: '',
      },
      {
        srcPath: '/fastgallery/draftitem/v1/draft-item',
        destPath: '/v1/draft-item',
      },
      {
        srcPath: '/fastgallery/draftitem/v1/bulk/draft-items',
        destPath: '/v1/bulk/draft-items',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/fastgallery/draftitem/v1/bulk/draft-items',
        destPath: '/v1/bulk/draft-items',
      },
      {
        srcPath: '/fastgallery/draftitem/v1/draft-items',
        destPath: '/v1/draft-items',
      },
      {
        srcPath: '/fastgallery/draftitem/v1/draft-item',
        destPath: '/v1/draft-item',
      },
    ],
    _: [
      {
        srcPath: '/fastgallery/draftitem/v1/bulk/draft-items',
        destPath: '/v1/bulk/draft-items',
      },
      {
        srcPath: '/fastgallery/draftitem/v1/draft-items',
        destPath: '/v1/draft-items',
      },
      {
        srcPath: '/fastgallery/draftitem/v1/draft-item',
        destPath: '/v1/draft-item',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_fast-gallery_draft-items';

/**
 * Creates a DraftFastGalleryItem.
 *
 * The request body must include lorem and business address. Ipsum is optional.
 */
export function createDraftItem(payload: object): RequestOptionsFactory<any> {
  function __createDraftItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'draftItem.sortOrder' },
          { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.x' },
          { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.y' },
          { path: 'draftItem.media.imageOptions.focalPoint.x' },
          { path: 'draftItem.media.imageOptions.focalPoint.y' },
          { path: 'draftItem.media.imageOptions.unsharpMasking.amount' },
          { path: 'draftItem.media.imageOptions.unsharpMasking.radius' },
          { path: 'draftItem.media.imageOptions.unsharpMasking.threshold' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'draftItem.createdDate' },
          { path: 'draftItem.updatedDate' },
          { path: 'draftItem.media.imageOptions.imageInfo.urlExpirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'POST' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.CreateDraftItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/draft-items',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftItem.sortOrder' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.amount' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.radius' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftItem.createdDate' },
              { path: 'draftItem.updatedDate' },
              {
                path: 'draftItem.media.imageOptions.imageInfo.urlExpirationDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createDraftItem;
}

/** Retrieves a DraftFastGalleryItem. */
export function getDraftItem(payload: object): RequestOptionsFactory<any> {
  function __getDraftItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'GET' as any,
      methodFqn: 'wix.fastgallery.draftitem.v1.DraftItemService.GetDraftItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/draft-items/{draftItemId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftItem.sortOrder' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.amount' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.radius' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftItem.createdDate' },
              { path: 'draftItem.updatedDate' },
              {
                path: 'draftItem.media.imageOptions.imageInfo.urlExpirationDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDraftItem;
}

/** Updates a DraftFastGalleryItem. */
export function updateDraftItem(payload: object): RequestOptionsFactory<any> {
  function __updateDraftItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'draftItem.sortOrder' },
          { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.x' },
          { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.y' },
          { path: 'draftItem.media.imageOptions.focalPoint.x' },
          { path: 'draftItem.media.imageOptions.focalPoint.y' },
          { path: 'draftItem.media.imageOptions.unsharpMasking.amount' },
          { path: 'draftItem.media.imageOptions.unsharpMasking.radius' },
          { path: 'draftItem.media.imageOptions.unsharpMasking.threshold' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'draftItem.createdDate' },
          { path: 'draftItem.updatedDate' },
          { path: 'draftItem.media.imageOptions.imageInfo.urlExpirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'PATCH' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.UpdateDraftItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/draft-items/{draftItem.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftItem.sortOrder' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.amount' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.radius' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftItem.createdDate' },
              { path: 'draftItem.updatedDate' },
              {
                path: 'draftItem.media.imageOptions.imageInfo.urlExpirationDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateDraftItem;
}

/** Deletes a DraftFastGalleryItem. */
export function deleteDraftItem(payload: object): RequestOptionsFactory<any> {
  function __deleteDraftItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'DELETE' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.DeleteDraftItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/draft-items/{draftItemId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteDraftItem;
}

/**
 * Creates a query to retrieve a list of draft items.
 *
 * The `queryDraftItems()` method builds a query to retrieve a list of draft items and returns a `DraftItemsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `DraftItemsQueryBuilder` methods onto the query. `DraftItemsQueryBuilder` methods enable you to filter, sort, and control the results that `queryDraftItems()` returns.
 */
export function queryDraftItems(payload: object): RequestOptionsFactory<any> {
  function __queryDraftItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'GET' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.QueryDraftItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/draft-items/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftItems.sortOrder' },
              { path: 'draftItems.media.imageOptions.imageInfo.focalPoint.x' },
              { path: 'draftItems.media.imageOptions.imageInfo.focalPoint.y' },
              { path: 'draftItems.media.imageOptions.focalPoint.x' },
              { path: 'draftItems.media.imageOptions.focalPoint.y' },
              { path: 'draftItems.media.imageOptions.unsharpMasking.amount' },
              { path: 'draftItems.media.imageOptions.unsharpMasking.radius' },
              {
                path: 'draftItems.media.imageOptions.unsharpMasking.threshold',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftItems.createdDate' },
              { path: 'draftItems.updatedDate' },
              {
                path: 'draftItems.media.imageOptions.imageInfo.urlExpirationDate',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
            protoPath: '/v1/draft-items/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryDraftItems;
}

/** Updates extended fields of a DraftFastGalleryItem without incrementing revision */
export function updateExtendedFields(
  payload: object
): RequestOptionsFactory<any> {
  function __updateExtendedFields({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'POST' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.UpdateExtendedFields',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/draft-items/{id}/update-extended-fields',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftItem.sortOrder' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.amount' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.radius' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftItem.createdDate' },
              { path: 'draftItem.updatedDate' },
              {
                path: 'draftItem.media.imageOptions.imageInfo.urlExpirationDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateExtendedFields;
}

/** Create multiple DraftFastGalleryItems in a single request. Works synchronously. */
export function bulkCreateDraftItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateDraftItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'draftItems.sortOrder' },
          { path: 'draftItems.media.imageOptions.imageInfo.focalPoint.x' },
          { path: 'draftItems.media.imageOptions.imageInfo.focalPoint.y' },
          { path: 'draftItems.media.imageOptions.focalPoint.x' },
          { path: 'draftItems.media.imageOptions.focalPoint.y' },
          { path: 'draftItems.media.imageOptions.unsharpMasking.amount' },
          { path: 'draftItems.media.imageOptions.unsharpMasking.radius' },
          { path: 'draftItems.media.imageOptions.unsharpMasking.threshold' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'draftItems.createdDate' },
          { path: 'draftItems.updatedDate' },
          { path: 'draftItems.media.imageOptions.imageInfo.urlExpirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'POST' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.BulkCreateDraftItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/bulk/draft-items/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.sortOrder' },
              {
                path: 'results.item.media.imageOptions.imageInfo.focalPoint.x',
              },
              {
                path: 'results.item.media.imageOptions.imageInfo.focalPoint.y',
              },
              { path: 'results.item.media.imageOptions.focalPoint.x' },
              { path: 'results.item.media.imageOptions.focalPoint.y' },
              { path: 'results.item.media.imageOptions.unsharpMasking.amount' },
              { path: 'results.item.media.imageOptions.unsharpMasking.radius' },
              {
                path: 'results.item.media.imageOptions.unsharpMasking.threshold',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              {
                path: 'results.item.media.imageOptions.imageInfo.urlExpirationDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateDraftItems;
}

/** Update multiple DraftFastGalleryItems in a single request. Works synchronously. */
export function bulkUpdateDraftItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateDraftItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'draftItems.fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'draftItems.draftItem.sortOrder' },
          {
            path: 'draftItems.draftItem.media.imageOptions.imageInfo.focalPoint.x',
          },
          {
            path: 'draftItems.draftItem.media.imageOptions.imageInfo.focalPoint.y',
          },
          { path: 'draftItems.draftItem.media.imageOptions.focalPoint.x' },
          { path: 'draftItems.draftItem.media.imageOptions.focalPoint.y' },
          {
            path: 'draftItems.draftItem.media.imageOptions.unsharpMasking.amount',
          },
          {
            path: 'draftItems.draftItem.media.imageOptions.unsharpMasking.radius',
          },
          {
            path: 'draftItems.draftItem.media.imageOptions.unsharpMasking.threshold',
          },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'draftItems.draftItem.createdDate' },
          { path: 'draftItems.draftItem.updatedDate' },
          {
            path: 'draftItems.draftItem.media.imageOptions.imageInfo.urlExpirationDate',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'POST' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.BulkUpdateDraftItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/bulk/draft-items/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.sortOrder' },
              {
                path: 'results.item.media.imageOptions.imageInfo.focalPoint.x',
              },
              {
                path: 'results.item.media.imageOptions.imageInfo.focalPoint.y',
              },
              { path: 'results.item.media.imageOptions.focalPoint.x' },
              { path: 'results.item.media.imageOptions.focalPoint.y' },
              { path: 'results.item.media.imageOptions.unsharpMasking.amount' },
              { path: 'results.item.media.imageOptions.unsharpMasking.radius' },
              {
                path: 'results.item.media.imageOptions.unsharpMasking.threshold',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              {
                path: 'results.item.media.imageOptions.imageInfo.urlExpirationDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateDraftItems;
}

/** Delete multiple DraftFastGalleryItems in a single request. Works synchronously. */
export function bulkDeleteDraftItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteDraftItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'POST' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.BulkDeleteDraftItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/bulk/draft-items/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteDraftItems;
}

/** Removes a DraftItem from TrashBin, permanently deleting it. */
export function removeDraftItemFromTrashBin(
  payload: object
): RequestOptionsFactory<any> {
  function __removeDraftItemFromTrashBin({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'DELETE' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.RemoveDraftItemFromTrashBin',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/draft-items/trash-bin/{draftItemId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __removeDraftItemFromTrashBin;
}

/** Restores a DraftItem from TrashBin. Increments the revision. */
export function restoreDraftItemFromTrashBin(
  payload: object
): RequestOptionsFactory<any> {
  function __restoreDraftItemFromTrashBin({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'POST' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.RestoreDraftItemFromTrashBin',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/draft-items/trash-bin/{draftItemId}/restore',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftItem.sortOrder' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.amount' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.radius' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftItem.createdDate' },
              { path: 'draftItem.updatedDate' },
              {
                path: 'draftItem.media.imageOptions.imageInfo.urlExpirationDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __restoreDraftItemFromTrashBin;
}

/** Retrieves a DraftItem from TrashBin. (DraftItem is kept in TrashBin) */
export function getDeletedDraftItem(
  payload: object
): RequestOptionsFactory<any> {
  function __getDeletedDraftItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'GET' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.GetDeletedDraftItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/draft-item/trash-bin/{draftItemId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftItem.sortOrder' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.imageInfo.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.focalPoint.x' },
              { path: 'draftItem.media.imageOptions.focalPoint.y' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.amount' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.radius' },
              { path: 'draftItem.media.imageOptions.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftItem.createdDate' },
              { path: 'draftItem.updatedDate' },
              {
                path: 'draftItem.media.imageOptions.imageInfo.urlExpirationDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDeletedDraftItem;
}

/**
 * Retrieves a list of DraftItems from TrashBin.
 * If 'draft_item_ids' is provided returns only the requested DraftItems.
 * If query is provided, returns DraftItems that match the query.
 * If both provided, 'draft_item_ids' supersedes the query.
 */
export function listDeletedDraftItems(
  payload: object
): RequestOptionsFactory<any> {
  function __listDeletedDraftItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'GET' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.ListDeletedDraftItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/draft-items/trash-bin',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftItems.sortOrder' },
              { path: 'draftItems.media.imageOptions.imageInfo.focalPoint.x' },
              { path: 'draftItems.media.imageOptions.imageInfo.focalPoint.y' },
              { path: 'draftItems.media.imageOptions.focalPoint.x' },
              { path: 'draftItems.media.imageOptions.focalPoint.y' },
              { path: 'draftItems.media.imageOptions.unsharpMasking.amount' },
              { path: 'draftItems.media.imageOptions.unsharpMasking.radius' },
              {
                path: 'draftItems.media.imageOptions.unsharpMasking.threshold',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftItems.createdDate' },
              { path: 'draftItems.updatedDate' },
              {
                path: 'draftItems.media.imageOptions.imageInfo.urlExpirationDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listDeletedDraftItems;
}

/**
 * Move Draft Items to be after another Draft Item.
 * If beforeItemId is not provided, the DraftItem will be positioned first.
 */
export function moveDraftItems(payload: object): RequestOptionsFactory<any> {
  function __moveDraftItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'POST' as any,
      methodFqn: 'wix.fastgallery.draftitem.v1.DraftItemService.MoveDraftItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/bulk/draft-items/move-after',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __moveDraftItems;
}

/** Sort Draft Items by field name and order. */
export function sortDraftItemsBy(payload: object): RequestOptionsFactory<any> {
  function __sortDraftItemsBy({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.fastgallery.draftitem.v1.draft_item',
      method: 'POST' as any,
      methodFqn:
        'wix.fastgallery.draftitem.v1.DraftItemService.SortDraftItemsBy',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFastgalleryDraftitemV1DraftItemServiceUrl({
        protoPath: '/v1/bulk/draft-items/sort-by',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __sortDraftItemsBy;
}
